import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Quote, Sparkles, BarChart3 } from "lucide-react";

export const STORIES = {
  rep: {
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_sales-practice-ai-1/artifacts/3zm68k5v_video_1790536489240.mp4",
    poster: "/roleplay-story-poster.jpg",
    duration: "0:18",
    label: "Rep story",
    eyebrow: "Why reps practise here",
    icon: Sparkles,
    quote: "I spent years guessing how to handle sales objections… Then I found Pitch Craft. It gives me a realistic AI customer to roleplay with, so I can test my pitch, get instant feedback, and sharpen my skills.",
    footnote: "Test your pitch, get instant feedback, run it again.",
  },
  manager: {
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_sales-practice-ai-1/artifacts/049ij808_video_1790806212626.mp4",
    poster: "/manager-story-poster.jpg",
    duration: "0:09",
    label: "Manager story",
    eyebrow: "Built for managers",
    icon: BarChart3,
    quote: "“How do I train remotely?” — “Pitch Craft. They practise handling objections and improve, while I track progress.” — “Even from the pool?”",
    footnote: "Your reps train anywhere, any time. You see every score, streak and weak spot from one team dashboard.",
  },
};

const EPIC = "bg-white rounded-3xl border border-slate-200/80 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]";

function StoryVideo({ story, testId }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { setStarted(true); v.play().catch(() => setPlaying(false)); } else v.pause();
  };
  return (
    <button type="button" onClick={toggle} aria-label={playing ? "Pause video" : "Play video"}
      className="group relative w-full sm:w-44 aspect-[9/16] sm:h-72 rounded-2xl overflow-hidden bg-slate-900 shadow-[0_12px_30px_-10px_rgba(15,23,42,0.35)] shrink-0" data-testid={`${testId}-toggle`}>
      <video ref={ref} src={story.src} poster={story.poster} playsInline preload="metadata"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
        className="absolute inset-0 w-full h-full object-cover" data-testid={`${testId}-video`} />
      {!playing && (
        <>
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-slate-900/70 to-transparent" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500 text-black shadow-[0_8px_30px_rgba(16,185,129,0.55)] transition-transform duration-300 group-hover:scale-110">
              <Play className="w-6 h-6 ml-0.5 fill-current" />
            </span>
          </span>
          <span className="absolute left-3 bottom-3 text-[10px] font-mono uppercase tracking-widest text-white/90">{started ? "Paused" : story.label} · {story.duration}</span>
        </>
      )}
      {playing && (
        <span className="absolute right-3 bottom-3 flex items-center justify-center w-8 h-8 rounded-full bg-white/90 text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity">
          <Pause className="w-4 h-4" />
        </span>
      )}
    </button>
  );
}

export function StoryCard({ story, cta, onCta, testId = "story-card", className = "" }) {
  const Icon = story.icon || Sparkles;
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }}
      className={`${EPIC} relative overflow-hidden p-4 sm:p-5 grid sm:grid-cols-[auto,1fr] gap-5 items-center ${className}`} data-testid={testId}>
      <div className="absolute -bottom-24 -right-16 w-64 h-64 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
      <StoryVideo story={story} testId={testId} />
      <div className="relative min-w-0">
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {story.eyebrow}</p>
        <Quote className="w-6 h-6 text-emerald-500/40 mt-3" />
        <p className="text-slate-700 text-base md:text-lg leading-relaxed mt-1 max-w-xl" data-testid={`${testId}-quote`}>{story.quote}</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {cta && (
            <button type="button" onClick={onCta} className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors" data-testid={`${testId}-cta`}>
              {cta}
            </button>
          )}
          <span className="text-xs text-slate-500">{story.footnote}</span>
        </div>
      </div>
    </motion.div>
  );
}

export function RoleplayStory({ onStart }) {
  return <StoryCard story={STORIES.rep} cta={<>Pick a customer below <span aria-hidden>↓</span></>} onCta={onStart} testId="roleplay-story" />;
}
